import { useRef, useState } from "react";
import s from "./VideoGallery.module.scss";
import { useLanguage } from "../../contexts/LanguageContext";

const getTikTokEmbedUrl = (url) => {
  const match = url.match(/\/video\/(\d+)/);
  const videoId = match?.[1];

  if (!videoId) return "";

  return `https://www.tiktok.com/embed/v2/${videoId}?lang=ja-JP`;
};

const getYouTubeEmbedUrl = (url) => {
  const match = url.match(/(?:shorts\/|youtu\.be\/|watch\?v=)([^?&/]+)/);
  const videoId = match?.[1];

  if (!videoId) return "";

  return `https://www.youtube.com/embed/${videoId}`;
};

const videos = [
  {
    key: "miyakoShorts",
    youtubeUrl: "https://youtube.com/shorts/c05zXrFEt_E?si=T5B7czfaf66FE69y",
  },
  {
    key: "openingBlue",
    tiktokUrl:
      "https://www.tiktok.com/@ren.world_tour/video/7663388962634976519",
  },
  {
    key: "sunsetSup",
    tiktokUrl:
      "https://www.tiktok.com/@ren.world_tour/video/7661855938634190088",
  },
  {
    key: "hiddenBeach",
    tiktokUrl:
      "https://www.tiktok.com/@ren.world_tour/video/7663163313026731271",
  },
  {
    key: "droneJourney",
    tiktokUrl:
      "https://www.tiktok.com/@ren.world_tour/video/7662402242791623943",
  },
  {
    key: "beach",
    tiktokUrl:
      "https://www.tiktok.com/@ren.world_tour/video/7663087974783012103",
  },
  {
    key: "miyakoBlue",
    tiktokUrl:
      "https://www.tiktok.com/@ren.world_tour/video/7662006023854509330",
  },
];

const TEXT = {
  JP: {
    kicker: "Videos",
    title: "Cinematic Moments",
    description:
      "宮古島で過ごした特別な時間。ドローン撮影やSUPツアー、サンセットの思い出をご紹介します。",
    videos: {
      openingBlue: "宮古島ムービー",
      miyakoShorts: "宮古島ショートムービー",
      sunsetSup: "サンセットSUP",
      hiddenBeach: "秘境ビーチ",
      droneJourney: "ドローン映像",
      beach: "ビーチ",
      miyakoBlue: "宮古ブルー",
    },
  },
  EN: {
    kicker: "Videos",
    title: "Cinematic Moments",
    description:
      "A collection of special moments in Miyako Island, from drone shots and SUP tours to unforgettable sunsets.",
    videos: {
      openingBlue: "Miyako Island Movie",
      miyakoShorts: "Miyako Island Short Movie",
      sunsetSup: "Sunset SUP",
      hiddenBeach: "Hidden Beach",
      droneJourney: "Drone Journey",
      beach: "Beach",
      miyakoBlue: "Miyako Blue",
    },
  },
  中文: {
    kicker: "视频",
    title: "电影般的瞬间",
    description:
      "这里展示在宫古岛度过的特别时光，包括无人机拍摄、SUP体验和日落回忆。",
    videos: {
      openingBlue: "宫古岛视频",
      miyakoShorts: "宫古岛短片",
      sunsetSup: "日落 SUP",
      hiddenBeach: "秘境海滩",
      droneJourney: "无人机旅程",
      beach: "海滩",
      miyakoBlue: "宫古蓝",
    },
  },
  한국어: {
    kicker: "영상",
    title: "시네마틱한 순간",
    description:
      "미야코지마에서 보낸 특별한 시간, 드론 촬영과 SUP 투어, 선셋의 추억을 소개합니다.",
    videos: {
      openingBlue: "미야코지마 영상",
      miyakoShorts: "미야코지마 쇼트 무비",
      sunsetSup: "선셋 SUP",
      hiddenBeach: "숨겨진 해변",
      droneJourney: "드론 영상",
      beach: "해변",
      miyakoBlue: "미야코 블루",
    },
  },
};

const VideoGallery = () => {
  const { lang } = useLanguage();
  const t = TEXT[lang] || TEXT.JP;
  const rowRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = () => {
    const row = rowRef.current;

    if (!row) return;

    const maxScroll = row.scrollWidth - row.clientWidth;
    const nextIndex =
      maxScroll > 0
        ? Math.round((row.scrollLeft / maxScroll) * (videos.length - 1))
        : 0;

    setActiveIndex(nextIndex);
  };

  const scrollToVideo = (index) => {
    const row = rowRef.current;

    if (!row) return;

    const maxScroll = row.scrollWidth - row.clientWidth;
    const left =
      videos.length > 1 ? (maxScroll / (videos.length - 1)) * index : 0;

    row.scrollTo({ left, behavior: "smooth" });
    setActiveIndex(index);
  };

  return (
    <section className={s.root}>
      <div className={s.heading}>
        <span className={s.kicker}>{t.kicker}</span>
        <h2>{t.title}</h2>
        <p>{t.description}</p>
      </div>

      <div className={s.galleryArea}>
        <div className={s.videoRow} ref={rowRef} onScroll={handleScroll}>
          {videos.map((video) => {
            const title = t.videos[video.key] || TEXT.JP.videos[video.key];
            const embedUrl = video.youtubeUrl
              ? getYouTubeEmbedUrl(video.youtubeUrl)
              : getTikTokEmbedUrl(video.tiktokUrl);

            return (
              <article key={video.key} className={s.card}>
                <div className={s.thumbnail}>
                  <iframe
                    className={s.socialEmbed}
                    src={embedUrl}
                    title={title}
                    loading="lazy"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </article>
            );
          })}
        </div>

        <div className={s.dots} aria-label="動画スライダー">
          {videos.map((video, index) => (
            <button
              key={video.key}
              type="button"
              className={index === activeIndex ? s.activeDot : ""}
              aria-label={`${index + 1}番目の動画を見る`}
              onClick={() => scrollToVideo(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default VideoGallery;
